import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiEdit3, FiMail, FiCopy, FiCheck } = FiIcons;

const ChapterKickstart = ({ data = {}, onUpdate, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    startingIdea: '',
    firstParagraph: '',
    coreConnection: '',
    ...data
  });

  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailToSelf = () => {
    const subject = encodeURIComponent('My First Chapter Draft - Planning with Purpose');
    const body = encodeURIComponent(`
My First Chapter Kickstart
==========================

Starting Idea/Scene:
${formData.startingIdea}

First Paragraph:
${formData.firstParagraph}

Connection to Core Message:
${formData.coreConnection}

---
Created with Planning with Purpose
Plan your book with clarity, calling, and confidence.
    `);
    
    window.open(`mailto:?subject=${subject}&body=${body}`);
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  const handleCopyToClipboard = async () => {
    const content = `My First Chapter Kickstart

Starting Idea/Scene:
${formData.startingIdea}

First Paragraph:
${formData.firstParagraph}

Connection to Core Message:
${formData.coreConnection}

---
Created with Planning with Purpose`;

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
    navigate('/summary');
  };

  const handleBack = () => {
    navigate('/scope');
  };

  const wordCount = formData.firstParagraph ? formData.firstParagraph.split(' ').length : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl font-bold text-stone-800 mb-3">
          Chapter Kickstart
        </h2>
        <p className="text-stone-600 text-lg">
          Take that first faithful step. Write with purpose and confidence.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiEdit3} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Your Starting Point</h3>
            </div>
            <textarea
              className="form-textarea"
              placeholder="What scene, idea, or truth will you start with? This could be a powerful opening line, a compelling scene, or the core truth you want to share..."
              value={formData.startingIdea}
              onChange={(e) => handleInputChange('startingIdea', e.target.value)}
            />
            <p className="text-xs text-stone-500 mt-2">
              💡 Don't overthink this - just capture the essence of how you want to begin
            </p>
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <SafeIcon icon={FiEdit3} className="text-brand-blue mr-2" />
                <h3 className="font-semibold text-stone-800">Your First Paragraph</h3>
              </div>
              <span className="text-sm text-stone-500">
                {wordCount} words
              </span>
            </div>
            <textarea
              className="form-textarea"
              style={{ minHeight: '150px' }}
              placeholder="Write your opening paragraph here. Don't worry about perfection - just let your heart and calling flow onto the page..."
              value={formData.firstParagraph}
              onChange={(e) => handleInputChange('firstParagraph', e.target.value)}
            />
            <div className="mt-3 flex justify-between items-center">
              <p className="text-xs text-stone-500">
                🎯 Aim for 100-300 words to get started
              </p>
              {wordCount >= 100 && wordCount <= 300 && (
                <span className="text-brand-orange text-sm font-medium">
                  Perfect length! ✨
                </span>
              )}
            </div>
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiEdit3} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Core Message Connection</h3>
            </div>
            <textarea
              className="form-textarea"
              placeholder="How does this opening connect to your core message? What deeper truth or transformation are you pointing toward?"
              value={formData.coreConnection}
              onChange={(e) => handleInputChange('coreConnection', e.target.value)}
            />
            <p className="text-xs text-stone-500 mt-2">
              🔗 This helps ensure your opening serves your book's greater purpose
            </p>
          </motion.div>
        </div>

        {/* Preview and Actions Panel */}
        <div className="lg:sticky lg:top-8 space-y-6">
          <motion.div 
            className="card bg-gradient-to-br from-brand-orange/10 to-brand-blue/10 border-brand-orange/20"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-serif text-xl font-bold text-stone-800 mb-4">
              Your First Chapter Draft
            </h3>
            
            {formData.firstParagraph ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-brand-orange/20">
                <div className="prose prose-stone max-w-none">
                  <p className="text-stone-700 leading-relaxed">
                    {formData.firstParagraph}
                  </p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-brand-orange/20">
                  <p className="text-sm text-stone-600">
                    <strong>Word count:</strong> {wordCount} words
                  </p>
                  {formData.coreConnection && (
                    <p className="text-sm text-stone-600 mt-2">
                      <strong>Core connection:</strong> {formData.coreConnection}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-brand-orange/20 text-center">
                <p className="text-stone-500 italic">
                  Your first paragraph will appear here as you write...
                </p>
              </div>
            )}
            
            {formData.firstParagraph && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-brand-blue/10 rounded-xl border border-brand-blue/20"
              >
                <p className="text-brand-blue text-sm font-medium">
                  🌟 Wonderful! You've taken the first step. God will bless your faithfulness!
                </p>
              </motion.div>
            )}
          </motion.div>

          {/* Action Buttons */}
          {formData.firstParagraph && (
            <motion.div 
              className="card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h4 className="font-medium text-stone-800 mb-4">Share Your Progress</h4>
              <div className="space-y-3">
                <button
                  onClick={handleEmailToSelf}
                  className="btn-secondary w-full flex items-center justify-center"
                >
                  <SafeIcon icon={emailSent ? FiCheck : FiMail} className="mr-2" />
                  {emailSent ? 'Email Prepared!' : 'Email to Myself'}
                </button>
                
                <button
                  onClick={handleCopyToClipboard}
                  className="btn-secondary w-full flex items-center justify-center"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="mr-2" />
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Encouragement */}
          <motion.div 
            className="card bg-gradient-to-br from-cream to-brand-orange/10 border-brand-orange/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="font-medium text-stone-800 mb-3">Remember</h4>
            <p className="text-stone-600 text-sm leading-relaxed">
              "Write the vision; make it plain on tablets." - Habakkuk 2:2
            </p>
            <p className="text-stone-600 text-sm leading-relaxed mt-2">
              You're not just writing words—you're stewarding a message God has entrusted to you. 
              Every sentence is a step of obedience.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Navigation */}
      <motion.div 
        className="flex justify-between mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <button
          onClick={handleBack}
          className="btn-secondary flex items-center"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Back to Project Scope
        </button>
        
        <button
          onClick={handleContinue}
          className="btn-primary flex items-center"
        >
          Continue to Summary
          <SafeIcon icon={FiArrowRight} className="ml-2" />
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ChapterKickstart;